import React, { createContext, useContext, useEffect } from 'react';
import { I18nManager, Platform } from 'react-native';

import { useLanguageStore } from '../stores/languageStore';

export interface RTLContextValue {
  isRTL: boolean;
  direction: 'ltr' | 'rtl';
  textAlign: 'left' | 'right' | 'center';
  flexDirection: 'row' | 'row-reverse';
  writingDirection: 'ltr' | 'rtl';
  // Layout helpers
  marginStart: (value: number) => { marginLeft?: number; marginRight?: number };
  marginEnd: (value: number) => { marginLeft?: number; marginRight?: number };
  paddingStart: (value: number) => { paddingLeft?: number; paddingRight?: number };
  paddingEnd: (value: number) => { paddingLeft?: number; paddingRight?: number };
  // Icon helpers
  getIconDirection: (iconName: string) => string;
  // Text helpers
  getTextAlign: (align?: 'start' | 'end' | 'center') => 'left' | 'right' | 'center';
}

const RTLContext = createContext<RTLContextValue | null>(null);

export const useRTL = (): RTLContextValue => {
  const context = useContext(RTLContext);
  if (!context) {
    throw new Error('useRTL must be used within RTLProvider');
  }
  return context;
};

interface RTLProviderProps {
  children: React.ReactNode;
}

export const RTLProvider: React.FC<RTLProviderProps> = ({ children }) => {
  const { currentLanguage, isLanguageRTL } = useLanguageStore();
  const isRTL = isLanguageRTL(currentLanguage);

  useEffect(() => {
    // Update React Native's I18nManager when language changes
    if (Platform.OS !== 'web') {
      const currentRTL = I18nManager.isRTL;
      if (currentRTL !== isRTL) {
        I18nManager.allowRTL(isRTL);
        I18nManager.forceRTL(isRTL);
        
        // Note: On React Native, changing RTL requires app restart
        // This is a limitation of React Native's I18nManager
        if (Platform.OS === 'android') {
          // On Android, we can restart the app programmatically
          // RNRestart.Restart(); // Would need react-native-restart package
        }
      }
    }
  }, [isRTL]);

  const contextValue: RTLContextValue = {
    isRTL,
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
    flexDirection: isRTL ? 'row-reverse' : 'row',
    writingDirection: isRTL ? 'rtl' : 'ltr',

    // Layout helpers
    marginStart: (value: number) => 
      isRTL ? { marginRight: value } : { marginLeft: value },
    
    marginEnd: (value: number) => 
      isRTL ? { marginLeft: value } : { marginRight: value },
    
    paddingStart: (value: number) => 
      isRTL ? { paddingRight: value } : { paddingLeft: value },
    
    paddingEnd: (value: number) => 
      isRTL ? { paddingLeft: value } : { paddingRight: value },

    // Icon helpers - flip directional icons for RTL
    getIconDirection: (iconName: string) => {
      const directionalIcons: Record<string, string> = {
        'arrow-left': isRTL ? 'arrow-right' : 'arrow-left',
        'arrow-right': isRTL ? 'arrow-left' : 'arrow-right',
        'chevron-left': isRTL ? 'chevron-right' : 'chevron-left',
        'chevron-right': isRTL ? 'chevron-left' : 'chevron-right',
        'angle-left': isRTL ? 'angle-right' : 'angle-left',
        'angle-right': isRTL ? 'angle-left' : 'angle-right',
        'caret-left': isRTL ? 'caret-right' : 'caret-left',
        'caret-right': isRTL ? 'caret-left' : 'caret-right',
      };
      
      return directionalIcons[iconName] || iconName;
    },

    // Text alignment helpers
    getTextAlign: (align?: 'start' | 'end' | 'center') => {
      if (align === 'center') return 'center';
      if (align === 'start') return isRTL ? 'right' : 'left';
      if (align === 'end') return isRTL ? 'left' : 'right';
      return isRTL ? 'right' : 'left'; // default
    }
  };

  return (
    <RTLContext.Provider value={contextValue}>
      {children}
    </RTLContext.Provider>
  );
};

// RTL-aware component wrapper
interface RTLViewProps {
  children: React.ReactNode;
  style?: any;
  reverse?: boolean; // Force reverse direction
}

export const RTLView: React.FC<RTLViewProps> = ({ children, style, reverse = false }) => {
  const { isRTL, flexDirection } = useRTL();
  
  const rtlStyle = {
    ...style,
    flexDirection: reverse ? (isRTL ? 'row' : 'row-reverse') : flexDirection
  };

  return (
    <div style={rtlStyle}>
      {children}
    </div>
  );
};

// RTL-aware text component
interface RTLTextProps {
  children: React.ReactNode;
  style?: any;
  align?: 'start' | 'end' | 'center';
}

export const RTLText: React.FC<RTLTextProps> = ({ children, style, align }) => {
  const { getTextAlign, writingDirection } = useRTL();
  
  const rtlStyle = {
    ...style,
    textAlign: getTextAlign(align),
    direction: writingDirection
  };

  return (
    <span style={rtlStyle}>
      {children}
    </span>
  );
};

// Tailwind CSS RTL utilities
export const useRTLStyles = () => {
  const { isRTL } = useRTL();
  
  return {
    // Margin utilities
    ml: (value: string) => isRTL ? `mr-${value}` : `ml-${value}`,
    mr: (value: string) => isRTL ? `ml-${value}` : `mr-${value}`,
    ms: (value: string) => isRTL ? `mr-${value}` : `ml-${value}`, // margin-start
    me: (value: string) => isRTL ? `ml-${value}` : `mr-${value}`, // margin-end
    
    // Padding utilities
    pl: (value: string) => isRTL ? `pr-${value}` : `pl-${value}`,
    pr: (value: string) => isRTL ? `pl-${value}` : `pr-${value}`,
    ps: (value: string) => isRTL ? `pr-${value}` : `pl-${value}`, // padding-start
    pe: (value: string) => isRTL ? `pl-${value}` : `pr-${value}`, // padding-end
    
    // Text alignment
    textLeft: isRTL ? 'text-right' : 'text-left',
    textRight: isRTL ? 'text-left' : 'text-right',
    textStart: isRTL ? 'text-right' : 'text-left',
    textEnd: isRTL ? 'text-left' : 'text-right',
    
    // Flex direction
    flexRow: isRTL ? 'flex-row-reverse' : 'flex-row',
    flexRowReverse: isRTL ? 'flex-row' : 'flex-row-reverse',
    
    // Border utilities
    borderL: isRTL ? 'border-r' : 'border-l',
    borderR: isRTL ? 'border-l' : 'border-r',
    borderS: isRTL ? 'border-r' : 'border-l', // border-start
    borderE: isRTL ? 'border-l' : 'border-r', // border-end
    
    // Rounded corners
    roundedL: isRTL ? 'rounded-r' : 'rounded-l',
    roundedR: isRTL ? 'rounded-l' : 'rounded-r',
    roundedTL: isRTL ? 'rounded-tr' : 'rounded-tl',
    roundedTR: isRTL ? 'rounded-tl' : 'rounded-tr',
    roundedBL: isRTL ? 'rounded-br' : 'rounded-bl',
    roundedBR: isRTL ? 'rounded-bl' : 'rounded-br',
  };
};

// Hook for RTL-aware animations
export const useRTLAnimations = () => {
  const { isRTL } = useRTL();
  
  return {
    slideInLeft: isRTL ? 'slideInRight' : 'slideInLeft',
    slideInRight: isRTL ? 'slideInLeft' : 'slideInRight',
    slideOutLeft: isRTL ? 'slideOutRight' : 'slideOutLeft',
    slideOutRight: isRTL ? 'slideOutLeft' : 'slideOutRight',
    
    // Transform values
    translateX: (value: number) => isRTL ? -value : value,
    rotateY: (value: number) => isRTL ? -value : value,
  };
};

// Font loading helper for RTL languages
export const useRTLFonts = () => {
  const { currentLanguage } = useLanguageStore();
  
  const getFontFamily = (language: string): string => {
    const fontMap: Record<string, string> = {
      'ar': 'NotoSansArabic',
      'he': 'NotoSansHebrew',
      'fa': 'NotoSansPersian',
      'ur': 'NotoSansUrdu',
      'am': 'NotoSansEthiopic',
      'ko': 'NotoSansKR',
      'default': 'System'
    };
    
    return fontMap[language] || fontMap['default'];
  };
  
  return {
    currentFont: getFontFamily(currentLanguage),
    getFontFamily,
    loadFont: async (language: string) => {
      // Font loading logic would go here
      // This could use expo-font or similar
      const fontFamily = getFontFamily(language);
      if (fontFamily !== 'System') {
        // Load custom font
        console.log(`Loading font: ${fontFamily} for language: ${language}`);
      }
    }
  };
};